import React, { ReactNode } from 'react'
import { ScrollView, ViewStyle } from 'react-native'

import Clipboard from '@react-native-clipboard/clipboard'
import { useToast } from '@zet/common/components/toast'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B2, B4, B5, H5, SH2, SH3 } from '@zet/design-system/typography'
import { Image } from 'expo-image'
import { LinearGradient } from 'expo-linear-gradient'
import moment from 'moment'

import Copy from '../../assets/images/icons/copy.svg'
import { brandsI18n } from '../../localization'
import { UserDetailsProps } from '../../types'
import { ICON_MAP } from '../../utils/iconMap'
import ActionButton from '../common/actionButtons/actionButton'

type Props = {
  data: any
  props: {
    status: {
      title?: string
      subTitle?: string
      image?: string
      subHeading?: string
      getImageByUrl?: boolean
      status?: ReactNode
      applicationType: 'pl' | 'hl' | 'hdfc'
      confetti?: boolean
      gradientConfig?: {
        backgroundGradient: LinearGradientProps
        detailsHeaderGradientConfig: LinearGradientProps
        detailsBodyConfig: {
          bgColor?: string
        }
      }
      textColor?: string
    }
    errorsList?: string[]
    c1Id?: string
    showC1Id?: boolean
    secondaryButton?: any
    showTimeLeft: boolean
    clickableRedirectUrl?: string
    redirect_url?: string
  }
  userDetails: UserDetailsProps
}

interface LinearGradientProps {
  colors?: string[]
  locations?: number[]
  start?: { x: number; y: number }
  end?: { x: number; y: number }
}
interface LinearGradientWrapper extends LinearGradientProps {
  isGradient: boolean
  children: ReactNode
  style?: ViewStyle
  classname?: string
}

const LinearGradientWrapper = ({
  isGradient,
  children,
  style,
  classname,
  colors = ['white', 'white'],
  ...props
}: LinearGradientWrapper) => {
  if (isGradient)
    return (
      <LinearGradient style={style} colors={colors} {...props}>
        {children}
      </LinearGradient>
    )
  return (
    <Stack className={classname} style={style}>
      {children}
    </Stack>
  )
}

interface LinearGradientProps {
  colors?: string[]
  locations?: number[]
  start?: { x: number; y: number }
  end?: { x: number; y: number }
}

const ApplicationStatus: React.FC<Props> = (_props) => {
  const { props, data, userDetails } = _props
  const toast = useToast()

  const Icon = ICON_MAP[props?.status?.image] || ICON_MAP.SERVER_ERROR
  const textColor = props?.status?.textColor || ''
  const gradientConfig = props?.status?.gradientConfig

  const leadSource = userDetails?.leadSource

  const copyToClipboard = () => {
    Clipboard.setString(props?.c1Id)
    toast?.show({
      heading: brandsI18n.t('axis.c1IdCopied'),
      intent: 'success',
    })
  }

  return (
    <LinearGradientWrapper
      isGradient={Boolean(gradientConfig?.backgroundGradient)}
      style={{ flex: 1 }}
      {...gradientConfig?.backgroundGradient}
    >
      {props?.status?.confetti && (
        <Image
          source={require('../../assets/images/icons/confetti.png')}
          style={{
            width: '100%',
            height: '50%',
            position: 'absolute',
            top: 0,
            zIndex: -1,
          }}
          contentFit="contain"
        />
      )}
      <ScrollView className="flex content-between justify-center">
        <Stack className="items-center justify-center">
          {Boolean(props?.status?.image) && (
            <Stack className="mb-4 h-32 w-32 items-center justify-center rounded-lg">
              <Icon />
            </Stack>
          )}
          {Boolean(props?.status?.getImageByUrl) && (
            <Stack className="mb-4 h-36 w-56 items-center justify-center rounded-lg">
              <ICON_MAP.TICK
                style={{
                  height: 48,
                  width: 48,
                  zIndex: 50,
                  position: 'absolute',
                  bottom: -24,
                }}
              />
              <Image
                source={{ uri: data?.metadata?.logoUrl }}
                style={{
                  height: '100%',
                  width: '100%',
                  borderRadius: 8,
                }}
                cachePolicy={'none'}
                contentFit="contain"
              />
            </Stack>
          )}
          {Boolean(props?.status?.title) && (
            <H5 className={`mx-3 mb-1 text-center ${textColor}`}>
              {props?.status?.title}
            </H5>
          )}
          {Boolean(props?.status?.subHeading) && (
            <SH2 className={`mx-3 mb-1 text-center ${textColor}`}>
              {props?.status?.subHeading}
            </SH2>
          )}

          {props?.status?.status && (
            <Stack
              className={`border-primary-10 bg-primary-10 my-3 w-[90%] overflow-hidden rounded-lg ${
                gradientConfig?.detailsHeaderGradientConfig ? '' : 'border'
              }`}
            >
              <LinearGradientWrapper
                {...gradientConfig?.detailsHeaderGradientConfig}
                classname="bg-primary-10"
                isGradient={Boolean(
                  gradientConfig?.detailsHeaderGradientConfig
                )}
                style={{ alignItems: 'center', justifyContent: 'center' }}
              >
                <SH2
                  className={`text-primary-200 py-1 text-center font-bold ${textColor}`}
                >
                  {brandsI18n.t(
                    `${props?.status?.applicationType}.yourApplicationDetails`
                  )}
                </SH2>
              </LinearGradientWrapper>
              <Stack
                className="bg-grays-10 px-2 "
                style={{
                  backgroundColor: gradientConfig?.detailsBodyConfig?.bgColor,
                }}
              >
                {data?.metadata?.productName && (
                  <XStack className="pt-2">
                    <B5 className={`text-grays-80 w-[50%] ${textColor}`}>
                      {brandsI18n.t(
                        `${props?.status?.applicationType}.productAppliedFor`
                      ) + ': '}
                      :
                    </B5>
                    <B4 className={`text-grays-80 ${textColor}`}>
                      {data?.metadata?.productName}
                    </B4>
                  </XStack>
                )}
                {data?.metadata?.arnNumber && (
                  <XStack className="pt-2">
                    <B5
                      className={`${
                        textColor ? textColor : 'text-grays-80'
                      } w-[50%] `}
                    >
                      {brandsI18n.t(
                        `${props?.status?.applicationType}.yourARN`
                      ) + ': '}
                    </B5>
                    <B4
                      className={`${textColor ? textColor : 'text-grays-80'}`}
                    >
                      {data?.metadata?.arnNumber}
                    </B4>
                  </XStack>
                )}
                {data?.metadata?.leadId && (
                  <XStack className="pt-2">
                    <B5
                      className={`${
                        textColor ? textColor : 'text-grays-80'
                      } w-[50%] `}
                    >
                      {brandsI18n.t(
                        `${props?.status?.applicationType}.leadId`
                      ) + ': '}
                    </B5>
                    <B4
                      className={`${textColor ? textColor : 'text-grays-80'}`}
                    >
                      {data?.metadata?.leadId}
                    </B4>
                  </XStack>
                )}
                {data?.metadata?.approvedOn && (
                  <XStack className="pt-2">
                    <B5
                      className={`${
                        textColor ? textColor : 'text-grays-80'
                      } w-[50%] `}
                    >
                      {brandsI18n.t(
                        `${props?.status?.applicationType}.approvedOn`
                      ) + ': '}
                    </B5>
                    <B4
                      className={`${textColor ? textColor : 'text-grays-80'}`}
                    >
                      {moment(data?.metadata?.approvedOn).format('Do MMM YYYY')}
                    </B4>
                  </XStack>
                )}
                <XStack className="py-2">
                  <B5
                    className={`${
                      textColor ? textColor : 'text-grays-80'
                    } w-[50%]`}
                  >
                    {brandsI18n.t(
                      `${props?.status?.applicationType}.applicationStatus`
                    ) + ': '}
                  </B5>
                  <B4 className={`${textColor ? textColor : 'text-grays-80'}`}>
                    {props?.status?.status}
                  </B4>
                </XStack>
              </Stack>
            </Stack>
          )}
          {Boolean(props?.status?.subTitle) && (
            <B2
              className={`mx-4 mt-3 w-[90%] text-center ${
                textColor || 'text-gray-700'
              }`}
            >
              {props?.status?.subTitle}
            </B2>
          )}
          {Boolean(props?.errorsList?.length) &&
            props?.errorsList?.map((error, index) => (
              <SH2 key={error} className={`${index === 0 ? 'mt-2' : ''}`}>{`${
                index + 1
              }.${error}`}</SH2>
            ))}
          {Boolean(props?.showTimeLeft) && (
            <Stack className="mx-4 items-center rounded-md bg-red-100 px-3 py-3">
              <XStack className="items-center justify-center">
                <ICON_MAP.CLOCK />
                <H5 className="text-center">{`${
                  data?.metadata?.timeLeft ?? 0
                } min left`}</H5>
              </XStack>
              <B2 className="text-center">
                {'Something went wrong. Please try again later.'}
              </B2>
            </Stack>
          )}
          {Boolean(props?.showC1Id && props?.c1Id) && (
            <>
              <B2 className={`my-3 ${textColor}`}>
                {brandsI18n.t('axis.c1IdFind')}
              </B2>
              <XStack className="bg-grays-20 items-center rounded px-2 py-2">
                <B5 className={`text-primary-200 ${textColor}`}>
                  {brandsI18n.t('axis.c1Id')}
                </B5>
                <B4 className={`text-primary-200 pr-1 ${textColor}`}>
                  {props?.c1Id}
                </B4>
                <Stack className="border-grays-40 mr-2 h-3 w-1 border-r"></Stack>
                <Pressable onPress={copyToClipboard}>
                  <XStack className="items-center">
                    <Copy className="pr-2" />
                    <SH3 className={`text-primary-100 pl-2 ${textColor}`}>
                      {brandsI18n.t('axis.c1IdCopy')}
                    </SH3>
                  </XStack>
                </Pressable>
              </XStack>
            </>
          )}
        </Stack>
        {props?.secondaryButton && leadSource !== 'plApp' && (
          <Stack className="my-2 self-center">
            <ActionButton
              type="secondary"
              title={props?.secondaryButton?.title}
              action={props?.secondaryButton?.action}
              nextStateRedirect={props?.secondaryButton?.nextStateRedirect}
              icon={props?.secondaryButton?.icon}
              redirectUrl={props?.clickableRedirectUrl ?? props?.redirect_url}
            />
          </Stack>
        )}
      </ScrollView>
      {props?.secondaryButton && leadSource === 'plApp' && (
        <Stack className="my-2 w-full bg-white">
          <ActionButton
            type="secondary"
            title={props?.secondaryButton?.title}
            action={props?.secondaryButton?.action}
            nextStateRedirect={props?.secondaryButton?.nextStateRedirect}
            icon={props?.secondaryButton?.icon}
            redirectUrl={props?.clickableRedirectUrl ?? props?.redirect_url}
          />
        </Stack>
      )}
    </LinearGradientWrapper>
  )
}

export default ApplicationStatus
